'use client'

import dynamic from 'next/dynamic'
import { ContentfulLivePreview } from '@contentful/live-preview'
import { useContentfulLiveUpdates } from '@contentful/live-preview/react'
import { Product } from '@cerifi/contentful/__generated/graphql.types'
import Content from '@cerifi/contentful/components/content'
import { Product as BCProduct } from '@cerifi/ecomm/bigcommerce/types/product'
import { colors } from '@cerifi/theme/colors'
import { RichText } from '@cerifi/ui/cms/helper-components/rich-text'
import { CmsBackgroundShape } from '@cerifi/ui/cms/image-shape/background-shape'
import { Container } from '@cerifi/ui/components/container'
import { Currency } from '@cerifi/ui/components/currency'

const AddToCart = dynamic(() => import('@cerifi/ui/components/add-to-cart'))
const ProductModifiers = dynamic(
  () => import('@cerifi/ui/components/product-modifiers')
)

interface ProductPageProps {
  ctfProduct: Product | null
  product: BCProduct
}

const DEFAULT_BG_COLOR = colors.tan[100]
const DEFAULT_TEXT_COLOR = colors.neutral[900]

export const ProductPageContent = (props: ProductPageProps) => {
  const productPage = useContentfulLiveUpdates(props.ctfProduct)

  return (
    <>
      <div
        className="relative mx-auto overflow-hidden px-4 py-8 text-center lg:px-12 lg:py-16"
        style={{
          backgroundColor:
            productPage?.accentColor?.backgroundColor || DEFAULT_BG_COLOR,
          color: productPage?.accentColor?.textColor || DEFAULT_TEXT_COLOR,
        }}
      >
        {productPage && productPage.backgroundShape?.shapeName && (
          <CmsBackgroundShape
            {...ContentfulLivePreview.getProps({
              entryId: productPage.sys.id,
              fieldId: 'backgroundShape',
              locale: 'en-US',
            })}
            content={productPage.backgroundShape as any}
            color={productPage.accentColor?.backgroundColor || DEFAULT_BG_COLOR}
          />
        )}

        <Container className="z2 relative">
          {props.product.categories && props.product.categories.length > 0 && (
            <div className="mb-6 text-sm lg:text-base font-medium text-neutral-700 uppercase tracking-wider">
              {props.product.categories[0]?.name}
            </div>
          )}
          {(productPage?.title || props.product?.name) && (
            <div className="flex-1">
              {productPage?.title ? (
                <h1
                  className="mb-6 text-4xl lg:text-5xl"
                  style={{
                    color: 'inherit',
                  }}
                  {...ContentfulLivePreview.getProps({
                    entryId: productPage?.sys.id,
                    fieldId: 'title',
                    locale: 'en-US',
                  })}
                >
                  {productPage.title}
                </h1>
              ) : (
                <h1
                  className="mb-6 text-4xl lg:text-5xl"
                  style={{
                    color: 'inherit',
                  }}
                >
                  {props.product.name}
                </h1>
              )}
            </div>
          )}

          <p className="mb-8 text-lg font-normal text-gray-500 dark:text-gray-400 sm:px-16 lg:text-xl xl:px-48">
            <Currency dollars={props.product.price.value} />
          </p>
          {
            // if the product has options, the add to cart button is in the ProductModifiers component
            props.product.options.length === 0 && (
              <div className="flex flex-col space-y-4 sm:flex-row sm:justify-center sm:space-x-4 sm:space-y-0">
                <AddToCart
                  product={props.product}
                  allowModifyQuantity={false}
                  className="mt-6"
                />
              </div>
            )
          }
        </Container>
      </div>

      {(productPage?.description ||
        props.product?.description ||
        !!props.product.options?.length ||
        !!productPage?.productDetailContentCollection) && (
        <Container>
          <div className="my-12 flex flex-col gap-12 sm:flex-row">
            <section className="flex-1 sm:flex-[2] flex gap-12 lg:gap-16 sm:flex-row flex-col-reverse max-w-[78.75rem] md:p-12 lg:px-20 xl:px-24">
              {(productPage?.description || props.product?.description) && (
                <div className="flex-1">
                  {productPage?.description ? (
                    <RichText
                      json={productPage.description.json}
                      links={productPage.description.links}
                      {...ContentfulLivePreview.getProps({
                        entryId: productPage.sys.id,
                        fieldId: 'description',
                        locale: 'en-US',
                      })}
                    />
                  ) : (
                    <div
                      className="text-base sm:text-lg rich-text"
                      dangerouslySetInnerHTML={{
                        __html: props.product.description,
                      }}
                    ></div>
                  )}
                </div>
              )}

              {!!props.product.options?.length && (
                <div className="flex-1">
                  <ProductModifiers product={props.product} />
                </div>
              )}
            </section>
          </div>

          {productPage?.productDetailContentCollection && (
            <Content
              contentCollection={productPage.productDetailContentCollection}
            />
          )}
        </Container>
      )}
    </>
  )
}
